/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    CacheType,
    createObservableDataAction,
    getCatalogId,
    IAction,
    IActionContext,
    IActionInput,
    IAny,
    ICommerceApiSettings,
    ICreateActionContext,
    IDictionary,
    IGeneric,
    IRequestContext
} from '@msdyn365-commerce/core';
import { getByIdsAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import { SimpleProduct } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';

import { ArrayExtensions, generateCacheKey } from './utilities';
import { QueryResultSettingsProxy } from './utilities/QueryResultSettingsProxy';
import { generateProductImageUrl } from './utilities/utils';

/**
 * Product Input.
 */
export class ProductInput implements IActionInput {
    public productId: number;

    public channelId: number;

    public warehouseId?: string;

    public catalogId?: number;

    private readonly apiSettings: ICommerceApiSettings;

    private readonly locale?: string;

    private readonly isLocaleCacheKeyDisabled: boolean;

    public constructor(
        productId: number | string,
        apiSettings: ICommerceApiSettings,
        channelId?: number,
        warehouseId?: string,
        requestContext?: IRequestContext,
        catalogId?: number
    ) {
        this.apiSettings = apiSettings;
        this.productId = +productId;
        this.channelId = channelId || apiSettings.channelId;
        this.warehouseId = warehouseId;
        this.catalogId = catalogId;
        this.isLocaleCacheKeyDisabled = requestContext?.features?.localeScopedProductCacheKey_KillSwitch === true;

        if (requestContext) {
            this.locale = requestContext.locale || '';

            if (catalogId === undefined) {
                this.catalogId = getCatalogId(requestContext);
            }
        }
    }

    public getCacheKey = () => {
        const shouldScopeByLocale = !this.isLocaleCacheKeyDisabled && this.locale !== undefined;

        return shouldScopeByLocale
            ? generateCacheKey(
                  this.apiSettings,
                  { key: 'ProductRecordId', value: this.productId },
                  { key: 'ChannelId', value: this.channelId },
                  { key: 'WarehouseId', value: this.warehouseId },
                  { key: 'CatalogId', value: this.catalogId },
                  { key: 'Locale', value: this.locale }
              )
            : generateCacheKey(
                  this.apiSettings,
                  { key: 'ProductRecordId', value: this.productId },
                  { key: 'ChannelId', value: this.channelId },
                  { key: 'WarehouseId', value: this.warehouseId },
                  { key: 'CatalogId', value: this.catalogId }
              );
    };

    public getCacheObjectType = () => 'SimpleProduct';

    public dataCacheType = (): CacheType => 'application';
}

/**
 * Creates the input required to make the retail api call.
 * @param inputData
 */
export const createSimpleProductsInput = (inputData: ICreateActionContext<IGeneric<IAny>>): IActionInput[] => {
    let productIds = inputData.config && inputData.config.productIds;
    if (!productIds) {
        return [];
    }

    productIds = typeof productIds === 'string' ? productIds.split(',') : productIds;
    return !Array.isArray(productIds)
        ? []
        : productIds.map((productId: string) => {
              return new ProductInput(productId, inputData.requestContext.apiSettings, undefined, undefined, inputData.requestContext);
          });
};

interface IGroupedProductRequest {
    catalogId?: number;
    channelId: number;
    productIds: number[];
    warehouseId?: string;
}

const getGroupedProductRequests = (
    productInputs: Pick<ProductInput, 'catalogId' | 'channelId' | 'productId' | 'warehouseId'>[]
): IGroupedProductRequest[] => {
    const groupedRequests = new Map<string, IGroupedProductRequest>();

    for (const productInput of productInputs) {
        const requestGroupKey = `${productInput.channelId}-${productInput.warehouseId ?? ''}-${productInput.catalogId ?? ''}`;
        let groupedRequest = groupedRequests.get(requestGroupKey);

        if (!groupedRequest) {
            groupedRequest = {
                catalogId: productInput.catalogId,
                channelId: productInput.channelId,
                productIds: [],
                warehouseId: productInput.warehouseId
            };
            groupedRequests.set(requestGroupKey, groupedRequest);
        }

        groupedRequest.productIds.push(productInput.productId);
    }

    return [...groupedRequests.values()];
};

const getQueryResultForRequestedProductIds = (ctx: IActionContext, requestedProductCount: number) => {
    const queryResult = QueryResultSettingsProxy.getPagingFromInputDataOrDefaultValue(ctx);

    if ((queryResult.Paging?.Top ?? 0) >= requestedProductCount) {
        return queryResult;
    }

    return {
        ...queryResult,
        Paging: {
            ...queryResult.Paging,
            Top: requestedProductCount
        }
    };
};

/**
 * Calls the Retail API and returns the product based on the passed ProductInput.
 * @param inputs
 * @param ctx
 */
export async function getSimpleProductsAction(inputs: ProductInput[], ctx: IActionContext): Promise<SimpleProduct[]> {
    if (!ArrayExtensions.hasElements(inputs)) {
        ctx.trace('[getSimpleProductsAction] Invalid or empty inputs passed.');
        return [];
    }

    const productIdMapping: IDictionary<number> = {};

    for (let index = 0; index < inputs.length; ++index) {
        productIdMapping[inputs[index].productId] = index;
    }

    const getProductPromises: Promise<SimpleProduct[]>[] = [];
    const groupedProductRequests = getGroupedProductRequests(inputs);
    for (const groupedProductRequest of groupedProductRequests) {
        const productIds = ArrayExtensions.unique(groupedProductRequest.productIds);
        const queryResult = getQueryResultForRequestedProductIds(ctx, productIds.length);

        getProductPromises.push(
            getByIdsAsync(
                { callerContext: ctx, queryResultSettings: queryResult },
                groupedProductRequest.channelId,
                productIds,
                groupedProductRequest.warehouseId ?? null,
                groupedProductRequest.catalogId ?? 0
            )
                .then(response => {
                    return response;
                })
                .catch(error => {
                    ctx.telemetry.debug(`[getSimpleProductsAction] Error in getting response from server. ${error}`);
                    return <SimpleProduct[]>{};
                })
        );
    }

    const products = ArrayExtensions.flatten(await Promise.all(getProductPromises));

    // For variants missing PrimaryImageUrl when OmniChannelMedia is enabled, fetch master product images as fallback.
    // When omni is disabled, Retail Server always provides PrimaryImageUrl via CDN naming convention.
    const isOmniEnabled = ctx.requestContext?.app?.config?.OmniChannelMedia;
    const variantsMissingImage = isOmniEnabled ? products.filter(p => !p.PrimaryImageUrl && p.MasterProductId) : [];
    if (ArrayExtensions.hasElements(variantsMissingImage)) {
        // Map variant RecordId to its SimpleProduct for O(1) lookup
        const variantByProductId = new Map(variantsMissingImage.map(p => [p.RecordId, p]));
        const inputsWithMissingImage = inputs.filter(input => variantByProductId.has(input.productId));
        const groupedMasterProductRequests = getGroupedProductRequests(
            inputsWithMissingImage.map(input => {
                const variant = variantByProductId.get(input.productId)!;

                return {
                    catalogId: input.catalogId,
                    channelId: input.channelId,
                    productId: variant.MasterProductId!,
                    warehouseId: input.warehouseId
                };
            })
        );

        try {
            const getMasterProductPromises: Promise<SimpleProduct[]>[] = [];
            for (const groupedMasterProductRequest of groupedMasterProductRequests) {
                const masterProductIds = ArrayExtensions.unique(groupedMasterProductRequest.productIds);
                const queryResult = getQueryResultForRequestedProductIds(ctx, masterProductIds.length);

                getMasterProductPromises.push(
                    getByIdsAsync(
                        { callerContext: ctx, queryResultSettings: queryResult },
                        groupedMasterProductRequest.channelId,
                        masterProductIds,
                        groupedMasterProductRequest.warehouseId ?? null,
                        groupedMasterProductRequest.catalogId ?? 0
                    )
                );
            }
            const masterProducts = ArrayExtensions.flatten(await Promise.all(getMasterProductPromises));
            const masterProductImageMap = new Map<number, string | undefined>(masterProducts.map(mp => [mp.RecordId, mp.PrimaryImageUrl]));
            for (const variant of variantsMissingImage) {
                const masterProductImageUrl = masterProductImageMap.get(variant.MasterProductId!);
                if (masterProductImageUrl) {
                    variant.PrimaryImageUrl = masterProductImageUrl;
                }
            }
        } catch (error) {
            ctx.telemetry.debug(`[getSimpleProductsAction] Error fetching master product images for fallback. ${error}`);
        }
    }

    const mappedProducts = products
        .map(product => {
            try {
                const newImageUrl = generateProductImageUrl(product, ctx.requestContext.apiSettings);

                if (newImageUrl) {
                    product.PrimaryImageUrl = newImageUrl;
                }

                return product;
            } catch {
                ctx.trace('[getSimpleProductsAction] Unable to update ImageURL for Product');
                return undefined;
            }
        })
        .reduce((memo: SimpleProduct[], product: SimpleProduct | undefined) => {
            if (!product) {
                return memo;
            }
            const index = productIdMapping[product.RecordId];
            memo[index] = product;
            return memo;
        }, []);

    // NOTE: <ZAFINE> This is not an ideal fix, but will resolve all current issues
    return inputs.map(input => {
        const foundProduct = mappedProducts.find(product => product && product.RecordId === input.productId);
        return foundProduct || <SimpleProduct>{};
    });
}

export default createObservableDataAction({
    id: '@msdyn365-commerce-modules/retail-actions/get-simple-products',
    action: <IAction<SimpleProduct[]>>getSimpleProductsAction,
    input: createSimpleProductsInput,
    isBatched: true
});
